// hub → Products — the whole catalogue, reviewable and editable.
//
// CEO 2026-10-07: "We should have a section on our app for product database —
// it should be editable so that we can move items around."
//
//   Table   every listing, 50 a page: photo, name, SKU, shelf, supplier and
//           whether it's on the website (and if not, why). Filter by search,
//           shelf, supplier and status; tick rows and move them to a shelf.
//   Drawer  one product: photos, name, shelf, our description beside the
//           supplier's, what holds it back, website on/off, other suppliers
//           and its history.
//
// A shelf move is kept when the catalogue is re-filed each night; an edited
// description is never overwritten by the writer. The supplier's own data is
// never changed. API: apps/api/src/routes/products-admin.ts.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, Boxes, Check, ChevronDown, ChevronLeft, ChevronRight, ExternalLink, Eye, EyeOff, Loader2, Search, Sparkles, X } from "lucide-react";
import { api, apiErrorMessage } from "@/lib/api";

/** Mirror apps/api/src/services/products-admin-rules.ts. */
const BULK_MOVE_MAX = 500;
const BULK_FILTER_MAX = 5000;
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/utils";
import { RunThroughStudio, STUDIO_JOB_MAX } from "@/pages/studio/RunThroughStudio";

// ─── Types (mirror apps/api/src/services/products-admin.ts) ──────────────────

interface ProductRow {
  id: string; sku: string | null; name: string;
  category: { slug: string; name: string; department: string | null } | null;
  supplier: string | null; image: string | null; imageCount: number;
  ready: boolean; gaps: string[]; descriptionSource: string; lowestCost: string | null;
  hidden: { shipping: boolean; byHand: boolean };
}
interface StatusCounts {
  total: number; ready: number; notReady: number; shippingHidden: number; handHidden: number;
  noPhoto: number; noCategory: number; supplierWords: number; ourWords: number;
  gaps: Record<string, number>; misfile: number | null;
}
interface ListResult { rows: ProductRow[]; total: number; offset: number; pageSize: number; counts: StatusCounts | null; misfileNeedsShelf: boolean }
interface ShelfChoice { slug: string; name: string; department: string | null; departmentSlug: string | null; listings: number }
interface Facets { shelves: ShelfChoice[]; suppliers: Array<{ id: string; name: string; listings: number }>; apparelShelves: string[] }
interface Detail extends ProductRow {
  description: string | null; descriptionGeneratedAt: string | null; supplierDescription: string | null; supplierStyle: string | null;
  brand: string | null; active: boolean; categorySetByHand: boolean; readinessCheckedAt: string | null;
  images: string[]; colours: string[];
  offers: Array<{ supplier: string; style: string | null; name: string; preferred: boolean; active: boolean; lowestCost: string | null }>;
  shippingHideReason: string | null;
  handHide: { reason: string | null; by: string | null; at: string } | null;
  moves: Array<{ from: string | null; to: string; by: string | null; at: string }>;
  edits: Array<{ field: string; by: string | null; at: string }>;
  editingReady: boolean;
}

// ─── Plain-English labels ────────────────────────────────────────────────────

/** Why a listing is held back from the website — short chip and full sentence. */
const GAPS: Record<string, [string, string]> = {
  offer: ["No live supplier", "No supplier is offering it right now, so it can't be ordered."],
  inactive: ["Switched off", "This product has been switched off."],
  category: ["Not on a shelf", "It isn't on a shelf the website shows. Move it to the right shelf."],
  price: ["No price", "The supplier hasn't given us a price we can use."],
  image: ["No photo", "There's no photo to show."],
  description: ["No description", "The description is missing or too short."],
  colour: ["Colours unclear", "We can't tell which colours it comes in, so the colour picker can't show them."],
};
const GAP_ORDER = ["offer", "inactive", "category", "price", "image", "description", "colour"];

type StatusKey = string;
function statusOptions(c: StatusCounts | null, misfileOk: boolean): Array<{ value: StatusKey; label: string; count?: number; disabled?: boolean }> {
  return [
    { value: "", label: "Any status", count: c?.total },
    { value: "ready", label: "Website-ready", count: c?.ready },
    { value: "not_ready", label: "Held back", count: c?.notReady },
    { value: "shipping_hidden", label: "Hidden: no shipping quote", count: c?.shippingHidden },
    { value: "hand_hidden", label: "Hidden by hand", count: c?.handHidden },
    { value: "no_photo", label: "No photo", count: c?.noPhoto },
    { value: "no_category", label: "Not on a shelf", count: c?.noCategory },
    { value: "supplier_words", label: "Supplier's description", count: c?.supplierWords },
    { value: "our_words", label: "Our description", count: c?.ourWords },
    ...GAP_ORDER.filter((g) => g !== "image").map((g) => ({ value: `gap_${g}`, label: `Held back: ${GAPS[g]![0].toLowerCase()}`, count: c?.gaps[g] })),
    {
      value: "misfile",
      label: misfileOk ? "Name doesn't fit the shelf" : "Name doesn't fit the shelf (choose a clothing shelf)",
      count: c?.misfile ?? undefined,
      disabled: !misfileOk,
    },
  ];
}

const n = (x: number) => x.toLocaleString("en-US");
const when = (iso: string) => new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
const shelfLabel = (c: ProductRow["category"]) => (c ? (c.department ? `${c.department} › ${c.name}` : c.name) : null);

// ─── Toasts ──────────────────────────────────────────────────────────────────

interface Toast { id: number; kind: "ok" | "error"; text: string }
function useToasts() {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const seq = useRef(0);
  const push = useCallback((kind: Toast["kind"], text: string) => {
    const id = ++seq.current;
    setToasts((t) => [...t, { id, kind, text }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), kind === "error" ? 7000 : 4000);
  }, []);
  const view = (
    <div className="pointer-events-none fixed bottom-6 right-6 z-[60] flex w-[min(380px,calc(100vw-48px))] flex-col gap-2" role="status" aria-live="polite">
      {toasts.map((t) => (
        <div key={t.id} className={cn("pointer-events-auto flex items-start gap-2 rounded-lg border px-4 py-3 text-label shadow-md", t.kind === "ok" ? "border-ok/30 bg-ok-bg text-ok" : "border-risk/30 bg-risk-bg text-risk")}>
          {t.kind === "ok" ? <Check className="mt-0.5 h-4 w-4 shrink-0" aria-hidden /> : <X className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />}
          <span>{t.text}</span>
        </div>
      ))}
    </div>
  );
  return { push, view };
}

// ─── Page ────────────────────────────────────────────────────────────────────

export function ProductsPage() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") ?? "";
  const category = params.get("shelf") ?? "";
  const vendor = params.get("supplier") ?? "";
  const status = params.get("status") ?? "";
  const page = Math.max(0, Number(params.get("page") ?? "0") || 0);
  const openId = params.get("product");

  const setParam = useCallback((patch: Record<string, string | null>, keepPage = false) => {
    setParams((p) => {
      const next = new URLSearchParams(p);
      for (const [k, v] of Object.entries(patch)) (v ? next.set(k, v) : next.delete(k));
      if (!keepPage && !("page" in patch)) next.delete("page");
      return next;
    }, { replace: true });
  }, [setParams]);

  const [facets, setFacets] = useState<Facets | null>(null);
  const [data, setData] = useState<ListResult | null>(null);
  const [counts, setCounts] = useState<StatusCounts | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [reload, setReload] = useState(0);
  const toasts = useToasts();

  useEffect(() => {
    api<Facets>("/hub-products/facets").then(setFacets).catch((e) => toasts.push("error", apiErrorMessage(e, "Could not load shelves and suppliers.")));
  }, [toasts.push]);

  // Search box: type freely, the list follows a moment later.
  const [typed, setTyped] = useState(q);
  useEffect(() => setTyped(q), [q]);
  useEffect(() => {
    if (typed.trim() === q) return;
    const t = setTimeout(() => setParam({ q: typed.trim() || null }), 300);
    return () => clearTimeout(t);
  }, [typed, q, setParam]);

  const apparel = facets?.apparelShelves.includes(category) ?? false;
  const effectiveStatus = status === "misfile" && !apparel ? "" : status;

  const latest = useRef(0);
  useEffect(() => {
    const ticket = ++latest.current;
    setLoading(true);
    setError(null);
    const qs = new URLSearchParams();
    if (q) qs.set("q", q);
    if (category) qs.set("category", category);
    if (vendor) qs.set("vendor", vendor);
    if (effectiveStatus) qs.set("status", effectiveStatus);
    if (page) qs.set("offset", String(page * 50));
    api<ListResult>(`/hub-products?${qs}`)
      .then((r) => {
        if (ticket !== latest.current) return;
        setData(r);
        if (r.counts) setCounts(r.counts);
      })
      .catch((e) => ticket === latest.current && setError(apiErrorMessage(e, "Could not load products.")))
      .finally(() => ticket === latest.current && setLoading(false));
  }, [q, category, vendor, effectiveStatus, page, reload]);

  // A new filter is a new list: forget ticks the person can no longer see.
  const [allMatching, setAllMatching] = useState(false);
  useEffect(() => { setSelected(new Set()); setAllMatching(false); }, [q, category, vendor, effectiveStatus]);

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / 50));
  const allOnPage = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const filtered = Boolean(q || category || vendor || status);

  const isTicked = (id: string) => allMatching || selected.has(id);
  const selectedCount = allMatching ? total : selected.size;
  const tooManyMatching = total > BULK_FILTER_MAX;
  const toggle = (id: string) => {
    if (allMatching) {
      // Unticking one row leaves "everything matching": keep this page, minus that row.
      setAllMatching(false);
      setSelected(new Set(rows.map((r) => r.id).filter((x) => x !== id)));
      return;
    }
    setSelected((s) => { const x = new Set(s); x.has(id) ? x.delete(id) : x.add(id); return x; });
  };
  const togglePage = () => {
    if (allMatching) { setAllMatching(false); setSelected(new Set()); return; }
    setSelected((s) => {
      const x = new Set(s);
      if (allOnPage) rows.forEach((r) => x.delete(r.id)); else rows.forEach((r) => x.add(r.id));
      return x;
    });
  };
  const clearSelection = () => { setSelected(new Set()); setAllMatching(false); };

  const [studioFor, setStudioFor] = useState<string[] | null>(null);
  const [confirmTo, setConfirmTo] = useState<string | null>(null);
  const [moving, setMoving] = useState(false);
  const bulkMove = async (slug: string) => {
    setMoving(true);
    const to = facets?.shelves.find((s) => s.slug === slug);
    // Everything matching goes as the filters themselves; the server finds
    // the same products the count above was taken from.
    const filter: Record<string, string> = {};
    if (q) filter.q = q;
    if (category) filter.category = category;
    if (vendor) filter.vendor = vendor;
    if (effectiveStatus) filter.status = effectiveStatus;
    const body = allMatching ? { filter, toSlug: slug } : { ids: [...selected], toSlug: slug };
    try {
      const r = await api<{ moved: number; skipped: number }>("/hub-products/bulk-move", { method: "POST", body: JSON.stringify(body) });
      toasts.push("ok", `Moved ${n(r.moved)} product${r.moved === 1 ? "" : "s"} to ${to ? shelfLabel({ slug, name: to.name, department: to.department }) : "the new shelf"}.${r.skipped ? ` ${n(r.skipped)} no longer exist and were skipped.` : ""}`);
      clearSelection();
      setReload((x) => x + 1);
    } catch (e) {
      toasts.push("error", apiErrorMessage(e, "Could not move those products. Nothing was changed."));
    } finally {
      setMoving(false);
      setConfirmTo(null);
    }
  };
  useEffect(() => { if (!selectedCount) setConfirmTo(null); }, [selectedCount]);
  const confirmShelf = confirmTo ? facets?.shelves.find((s) => s.slug === confirmTo) : undefined;

  // The drawer opens from a row and, on close, hands focus back to that row.
  const openProduct = (id: string) => setParam({ product: id }, true);
  const closeProduct = useCallback(() => {
    const id = openId;
    setParam({ product: null }, true);
    requestAnimationFrame(() => document.querySelector<HTMLButtonElement>(`button[data-open="${id}"]`)?.focus());
  }, [openId, setParam]);

  const onSaved = (d: Detail) => {
    setData((prev) => prev && { ...prev, rows: prev.rows.map((r) => (r.id === d.id ? toRow(d) : r)) });
  };

  return (
    <div className="mx-auto w-full max-w-[1320px] px-6 py-bay md:px-10">
      <header className="flex flex-col gap-3 border-b border-rule pb-gutter">
        <div className="flex flex-wrap items-center gap-3">
          <Boxes className="h-6 w-6" strokeWidth={1.5} aria-hidden />
          <h1 className="text-[32px] font-semibold leading-[1.15] tracking-[-1px]">Products</h1>
          <Link to="/studio" className="ml-auto inline-flex items-center gap-1 text-meta underline underline-offset-2">
            Catalogue health, photos and description drafts <ArrowRight className="h-3.5 w-3.5" aria-hidden />
          </Link>
        </div>
        <p className="max-w-[70ch] text-body text-ink-muted">
          Every product in our catalogue. Find one, fix its name, description or shelf, or move many at once.
          Your changes are kept when the catalogue is refreshed each night; the supplier's own information is never changed.
        </p>
      </header>

      {/* Filters */}
      <div className="sticky top-[68px] z-10 -mx-2 mt-gutter flex flex-wrap items-end gap-3 bg-paper/95 px-2 py-3 backdrop-blur">
        <label className="flex min-w-[240px] flex-1 flex-col gap-1">
          <span className="text-meta font-semibold uppercase tracking-wide text-ink-muted">Search</span>
          <span className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted" aria-hidden />
            <input
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder="Name, SKU or supplier style number"
              className="h-10 w-full rounded-md border border-rule bg-white pl-9 pr-8 text-label placeholder:text-ink-muted/70 focus-visible:outline focus-visible:outline-2 focus-visible:outline-focus"
            />
            {typed && (
              <button type="button" aria-label="Clear search" onClick={() => { setTyped(""); setParam({ q: null }); }} className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-ink-muted hover:text-ink">
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </span>
        </label>
        <FilterSelect label="Shelf" value={category} onChange={(v) => setParam({ shelf: v || null, status: status === "misfile" && !facets?.apparelShelves.includes(v) ? null : status || null })} width="w-[230px]">
          <option value="">All shelves</option>
          {groupShelves(facets?.shelves ?? []).map(([dept, items]) => (
            <optgroup key={dept.slug} label={dept.name}>
              <option value={dept.slug}>All of {dept.name}</option>
              {items.map((s) => <option key={s.slug} value={s.slug}>{s.name}</option>)}
            </optgroup>
          ))}
        </FilterSelect>
        <FilterSelect label="Supplier" value={vendor} onChange={(v) => setParam({ supplier: v || null })} width="w-[200px]">
          <option value="">All suppliers</option>
          {(facets?.suppliers ?? []).map((s) => <option key={s.id} value={s.id}>{s.name} ({n(s.listings)})</option>)}
        </FilterSelect>
        <FilterSelect label="Status" value={effectiveStatus} onChange={(v) => setParam({ status: v || null })} width="w-[250px]">
          {statusOptions(counts, apparel).map((o) => (
            <option key={o.value} value={o.value} disabled={o.disabled}>{o.label}{o.count !== undefined ? ` (${n(o.count)})` : ""}</option>
          ))}
        </FilterSelect>
        {filtered && (
          <button type="button" className="h-10 text-meta underline underline-offset-2" onClick={() => { setTyped(""); setParams(new URLSearchParams(), { replace: true }); }}>
            Clear filters
          </button>
        )}
      </div>

      {/* Summary or bulk actions */}
      <div className="mt-3 flex min-h-[44px] flex-wrap items-center gap-3">
        {selectedCount > 0 ? (
          <div className="flex w-full flex-wrap items-center gap-3 rounded-lg border border-ink bg-white px-4 py-2">
            <span className="text-label font-semibold">{allMatching ? `All ${n(selectedCount)} matching` : `${n(selectedCount)} selected`}</span>
            {confirmTo ? (
              <>
                <span className="text-label">
                  Move {selectedCount === 1 ? "it" : `all ${n(selectedCount)}`} to <b className="font-semibold">{confirmShelf ? shelfLabel({ slug: confirmTo, name: confirmShelf.name, department: confirmShelf.department }) : confirmTo}</b>?
                </span>
                <Button size="sm" disabled={moving} onClick={() => void bulkMove(confirmTo)}>
                  {moving ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : <ArrowRight className="h-3.5 w-3.5" aria-hidden />} Move
                </Button>
                <Button size="sm" variant="ghost" disabled={moving} onClick={() => setConfirmTo(null)}>Cancel</Button>
              </>
            ) : (
              <ShelfPicker
                shelves={facets?.shelves ?? []}
                label="Move to…"
                disabled={!allMatching && selected.size > BULK_MOVE_MAX}
                onPick={setConfirmTo}
              />
            )}
            {!confirmTo && (
              <Button
                size="sm"
                variant="outline"
                disabled={allMatching || selected.size > STUDIO_JOB_MAX}
                title={allMatching || selected.size > STUDIO_JOB_MAX ? `Studio runs up to ${STUDIO_JOB_MAX} ticked products at a time` : "Photos and descriptions, checked, waiting for your review"}
                onClick={() => setStudioFor([...selected])}
              >
                <Sparkles className="h-3.5 w-3.5" aria-hidden /> Run through Studio
              </Button>
            )}
            {!allMatching && selected.size > BULK_MOVE_MAX && <span className="text-meta text-risk">Tick up to {n(BULK_MOVE_MAX)} by hand, or use “select all matching”.</span>}
            <button type="button" className="ml-auto text-meta underline underline-offset-2" onClick={clearSelection}>Clear selection</button>
            {allOnPage && !allMatching && total > rows.length && (
              <p className="w-full text-meta text-ink-muted">
                All {n(rows.length)} on this page are selected.{" "}
                {tooManyMatching
                  ? <>These filters match {n(total)}; narrow them to {n(BULK_FILTER_MAX)} or fewer to move them all at once.</>
                  : <button type="button" className="font-semibold text-ink underline underline-offset-2" onClick={() => setAllMatching(true)}>Select all {n(total)} matching</button>}
              </p>
            )}
            {allMatching && (
              <p className="w-full text-meta text-ink-muted">
                Every product these filters match is selected, including other pages.{" "}
                <button type="button" className="font-semibold text-ink underline underline-offset-2" onClick={() => { setAllMatching(false); setSelected(new Set(rows.map((r) => r.id))); }}>Select only this page</button>
              </p>
            )}
          </div>
        ) : (
          <p className="text-meta text-ink-muted">
            {data ? (
              <>
                <b className="font-semibold text-ink">{n(total)}</b> product{total === 1 ? "" : "s"}
                {counts && !effectiveStatus ? <> · {n(counts.ready)} website-ready · {n(counts.notReady)} held back</> : null}
                {total > 50 ? <> · showing {n(page * 50 + 1)}–{n(Math.min(total, page * 50 + rows.length))}</> : null}
              </>
            ) : "Loading…"}
            {apparel && status !== "misfile" && counts?.misfile ? (
              <> · <button type="button" className="underline underline-offset-2" onClick={() => setParam({ status: "misfile" })}>{n(counts.misfile)} whose names don't fit this shelf</button></>
            ) : null}
          </p>
        )}
      </div>

      {error && <p className="mt-3 text-label text-risk">{error}</p>}

      {/* Table */}
      <div className="mt-2 overflow-x-auto rounded-lg border border-rule bg-white">
        <table className="w-full min-w-[920px] text-left text-meta">
          <caption className="sr-only">Products</caption>
          <thead className="bg-paper text-ink-muted">
            <tr>
              <th scope="col" className="w-10 px-3 py-2.5">
                <input type="checkbox" aria-label="Select every product on this page" checked={allMatching || allOnPage} onChange={togglePage} disabled={!rows.length} className="h-4 w-4 align-middle" />
              </th>
              <th scope="col" className="w-14 py-2.5"><span className="sr-only">Photo</span></th>
              <th scope="col" className="px-3 py-2.5 font-semibold">Product</th>
              <th scope="col" className="px-3 py-2.5 font-semibold">Shelf</th>
              <th scope="col" className="px-3 py-2.5 font-semibold">Supplier</th>
              <th scope="col" className="px-3 py-2.5 font-semibold">Website</th>
            </tr>
          </thead>
          <tbody className={cn(loading && "opacity-60 transition-opacity")}>
            {rows.map((r) => (
              <tr
                key={r.id}
                onClick={() => openProduct(r.id)}
                className={cn(
                  "cursor-pointer border-t border-rule hover:bg-paper [&:has(:focus-visible)]:bg-paper [&:has(button:focus-visible)]:outline [&:has(button:focus-visible)]:outline-2 [&:has(button:focus-visible)]:-outline-offset-2 [&:has(button:focus-visible)]:outline-focus",
                  (openId === r.id || isTicked(r.id)) && "bg-paper",
                )}
              >
                <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                  <input type="checkbox" aria-label={`Select ${r.name}`} checked={isTicked(r.id)} onChange={() => toggle(r.id)} className="h-4 w-4 align-middle" />
                </td>
                <td className="py-2">
                  <div className="h-10 w-10 overflow-hidden rounded border border-rule bg-paper">
                    {r.image && <img src={r.image} alt="" loading="lazy" className="h-full w-full object-contain" />}
                  </div>
                </td>
                <td className="max-w-[380px] px-3 py-2">
                  <button
                    type="button"
                    data-open={r.id}
                    onClick={(e) => { e.stopPropagation(); openProduct(r.id); }}
                    className="block w-full truncate text-left text-label font-semibold text-ink focus-visible:outline-none"
                    title={r.name}
                  >
                    {r.name}
                  </button>
                  <span className="flex items-center gap-2 text-ink-muted">
                    {r.sku ?? "No SKU yet"}
                    {r.imageCount <= 1 && !r.gaps.includes("image") && (
                      <span className="rounded bg-warn-bg px-1.5 text-meta font-semibold text-warn" title="Clients see better with several photos">
                        {r.imageCount === 0 ? "No photo" : "1 photo"}
                      </span>
                    )}
                  </span>
                </td>
                <td className="px-3 py-2">{shelfLabel(r.category) ?? <span className="text-risk">Not on a shelf</span>}</td>
                <td className="px-3 py-2">{r.supplier ?? <span className="text-ink-muted">None</span>}</td>
                <td className="px-3 py-2"><StatusChips row={r} /></td>
              </tr>
            ))}
          </tbody>
        </table>
        {loading && !rows.length && <p className="flex items-center gap-2 p-6 text-label text-ink-muted"><Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Loading products…</p>}
        {!loading && !rows.length && !error && (
          <p className="p-6 text-label text-ink-muted">
            {data?.misfileNeedsShelf ? "Choose a clothing shelf to check for names that don't fit it." : "No products match these filters."}
          </p>
        )}
      </div>

      {total > 50 && (
        <nav className="mt-gutter flex items-center justify-center gap-3" aria-label="Pages">
          <Button size="sm" variant="outline" disabled={page === 0 || loading} onClick={() => setParam({ page: page - 1 ? String(page - 1) : null }, true)}>
            <ChevronLeft className="h-4 w-4" aria-hidden /> Previous
          </Button>
          <span className="text-meta text-ink-muted">Page {n(page + 1)} of {n(pageCount)}</span>
          <Button size="sm" variant="outline" disabled={page + 1 >= pageCount || loading} onClick={() => setParam({ page: String(page + 1) }, true)}>
            Next <ChevronRight className="h-4 w-4" aria-hidden />
          </Button>
        </nav>
      )}

      {openId && (
        <ProductDrawer
          key={openId}
          id={openId}
          shelves={facets?.shelves ?? []}
          apparelShelves={facets?.apparelShelves ?? []}
          onClose={closeProduct}
          onSaved={onSaved}
          toast={toasts.push}
        />
      )}
      {studioFor && (
        <RunThroughStudio ids={studioFor} onClose={() => setStudioFor(null)} onStarted={(msg) => { toasts.push("ok", msg); clearSelection(); }} />
      )}
      {toasts.view}
    </div>
  );
}

function toRow(d: Detail): ProductRow {
  return {
    id: d.id, sku: d.sku, name: d.name, category: d.category, supplier: d.supplier, image: d.image, imageCount: d.imageCount,
    ready: d.ready, gaps: d.gaps, descriptionSource: d.descriptionSource, lowestCost: d.lowestCost, hidden: d.hidden,
  };
}

function FilterSelect({ label, value, onChange, width, children }: { label: string; value: string; onChange: (v: string) => void; width: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-meta font-semibold uppercase tracking-wide text-ink-muted">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={cn("h-10 rounded-md border border-rule bg-white px-2 text-label focus-visible:outline focus-visible:outline-2 focus-visible:outline-focus", width, value && "border-ink")}
      >
        {children}
      </select>
    </label>
  );
}

function StatusChips({ row: r }: { row: ProductRow }) {
  const chip = "inline-block whitespace-nowrap rounded px-1.5 py-0.5 text-meta font-semibold";
  if (r.hidden.byHand) return <span className={cn(chip, "bg-risk-bg text-risk")}>Hidden by hand</span>;
  if (r.ready && r.hidden.shipping) return <span className={cn(chip, "bg-warn-bg text-warn")} title="We can't quote shipping for it yet">Hidden: no shipping quote</span>;
  if (r.ready) return <span className={cn(chip, "bg-ok-bg text-ok")}>Website-ready</span>;
  const gaps = GAP_ORDER.filter((g) => r.gaps.includes(g));
  return (
    <span className="flex flex-wrap gap-1">
      {gaps.slice(0, 3).map((g) => <span key={g} className={cn(chip, "bg-warn-bg text-warn")} title={GAPS[g]![1]}>{GAPS[g]![0]}</span>)}
      {gaps.length > 3 && <span className={cn(chip, "bg-paper text-ink-muted")}>+{gaps.length - 3}</span>}
      {gaps.length === 0 && <span className={cn(chip, "bg-paper text-ink-muted")} title="Not checked since its last change">Held back</span>}
    </span>
  );
}

// ─── Shelf picker (searchable, grouped by department) ────────────────────────

function groupShelves(shelves: ShelfChoice[]): Array<[ShelfChoice, ShelfChoice[]]> {
  const roots = shelves.filter((s) => !s.department);
  return roots.map((r) => [r, shelves.filter((s) => s.departmentSlug === r.slug)] as [ShelfChoice, ShelfChoice[]]);
}

function ShelfPicker({ shelves, label, current, disabled, onPick, onOpenChange }: {
  shelves: ShelfChoice[]; label: string; current?: string; disabled?: boolean; onPick: (slug: string) => void;
  /** Lets a host (the drawer) leave Esc to the picker while it is open. */
  onOpenChange?: (open: boolean) => void;
}) {
  const [open, setOpen] = useState(false);
  useEffect(() => { onOpenChange?.(open); }, [open, onOpenChange]);
  const [find, setFind] = useState("");
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => { if (box.current && !box.current.contains(e.target as Node)) setOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", close); document.removeEventListener("keydown", esc); };
  }, [open]);

  const groups = useMemo(() => {
    const f = find.trim().toLowerCase();
    return groupShelves(shelves)
      .map(([dept, items]) => {
        const deptHit = !f || dept.name.toLowerCase().includes(f);
        const hits = items.filter((s) => deptHit || s.name.toLowerCase().includes(f));
        return [dept, hits, deptHit] as const;
      })
      .filter(([, hits, deptHit]) => deptHit || hits.length > 0);
  }, [shelves, find]);

  const pick = (slug: string) => { setOpen(false); setFind(""); onPick(slug); };
  const item = "flex w-full items-center justify-between gap-2 rounded px-2 py-1.5 text-left text-label hover:bg-paper focus-visible:bg-paper focus-visible:outline-none disabled:cursor-default disabled:opacity-50";

  return (
    <div className="relative" ref={box}>
      <Button size="sm" variant="primary" disabled={disabled || !shelves.length} onClick={() => setOpen((o) => !o)} aria-expanded={open} aria-haspopup="listbox">
        {label} <ChevronDown className="h-3.5 w-3.5" aria-hidden />
      </Button>
      {open && (
        <div className="absolute left-0 top-full z-50 mt-1 flex max-h-[420px] w-[320px] flex-col rounded-lg border border-rule bg-white shadow-md">
          <div className="border-b border-rule p-2">
            <input
              autoFocus
              value={find}
              onChange={(e) => setFind(e.target.value)}
              placeholder="Find a shelf"
              aria-label="Find a shelf"
              className="h-9 w-full rounded-md border border-rule px-2 text-label focus-visible:outline focus-visible:outline-2 focus-visible:outline-focus"
            />
          </div>
          <div className="overflow-y-auto p-1" role="listbox">
            {groups.length === 0 && <p className="p-3 text-meta text-ink-muted">No shelf by that name.</p>}
            {groups.map(([dept, hits, deptHit]) => (
              <div key={dept.slug} className="py-1">
                <p className="px-2 pb-1 pt-1.5 text-meta font-semibold uppercase tracking-wide text-ink-muted">{dept.name}</p>
                {deptHit && <button type="button" role="option" aria-selected={current === dept.slug} className={item} disabled={current === dept.slug} onClick={() => pick(dept.slug)}>
                  <span>{dept.name} <span className="text-ink-muted">(department only)</span></span>
                  {current === dept.slug && <span className="text-meta text-ink-muted">here now</span>}
                </button>}
                {hits.map((s) => (
                  <button key={s.slug} type="button" role="option" aria-selected={current === s.slug} className={item} disabled={current === s.slug} onClick={() => pick(s.slug)}>
                    <span>{s.name}</span>
                    <span className="text-meta text-ink-muted">{current === s.slug ? "here now" : n(s.listings)}</span>
                  </button>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── The drawer: one product ─────────────────────────────────────────────────

function ProductDrawer({ id, shelves, apparelShelves, onClose, onSaved, toast }: {
  id: string; shelves: ShelfChoice[]; apparelShelves: string[];
  onClose: () => void; onSaved: (d: Detail) => void; toast: (kind: "ok" | "error", text: string) => void;
}) {
  const [d, setD] = useState<Detail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [busy, setBusy] = useState<null | "name" | "desc" | "shelf" | "website">(null);
  const panel = useRef<HTMLDivElement>(null);

  const take = useCallback((x: Detail) => {
    setD(x);
    setName(x.name);
    setDesc(x.description ?? "");
  }, []);

  useEffect(() => {
    api<Detail>(`/hub-products/${id}`).then(take).catch((e) => setError(apiErrorMessage(e, "Could not load this product.")));
  }, [id, take]);

  const save = async (what: "name" | "desc" | "shelf", body: Record<string, string>, ok: string) => {
    setBusy(what);
    try {
      const x = await api<Detail>(`/hub-products/${id}`, { method: "PATCH", body: JSON.stringify(body) });
      take(x);
      onSaved(x);
      toast("ok", ok);
    } catch (e) {
      toast("error", apiErrorMessage(e, "Could not save. Nothing was changed."));
    } finally {
      setBusy(null);
    }
  };

  const [hiding, setHiding] = useState<string | null>(null);
  const setWebsite = async (hidden: boolean) => {
    const reason = hidden ? hiding?.trim() || undefined : undefined;
    setBusy("website");
    try {
      const x = await api<Detail>(`/hub-products/${id}/website`, { method: "POST", body: JSON.stringify({ hidden, reason }) });
      take(x);
      onSaved(x);
      setHiding(null);
      toast("ok", hidden ? "Hidden from the website. Reps can still quote it." : "Shown on the website again (if it passes the website checks).");
    } catch (e) {
      toast("error", apiErrorMessage(e, "Could not change that."));
    } finally {
      setBusy(null);
    }
  };

  const tidyName = name.replace(/\s+/g, " ").trim();
  const nameDirty = d !== null && tidyName !== d.name;
  const nameOk = tidyName.length >= 3 && tidyName.length <= 200;
  const descDirty = d !== null && desc.trim() !== (d.description ?? "").trim();
  const descLen = desc.trim().length;
  const descOk = descLen >= 40 && descLen <= 2000;
  const apparel = d?.category ? apparelShelves.includes(d.category.slug) : false;
  const onWebsite = d ? d.ready && !d.hidden.shipping && !d.hidden.byHand : false;

  // Closing with unsaved words asks first — backdrop, Esc and the X alike.
  const dirty = nameDirty || descDirty;
  const requestClose = useCallback(() => {
    if (dirty && !window.confirm("Discard your changes?")) return;
    onClose();
  }, [dirty, onClose]);
  const pickerOpen = useRef(false);
  const onPickerOpenChange = useCallback((o: boolean) => { pickerOpen.current = o; }, []);
  useEffect(() => { panel.current?.focus(); }, []);
  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (!pickerOpen.current) requestClose();
        return;
      }
      // Keep Tab inside the drawer while it is open.
      if (e.key !== "Tab" || !panel.current) return;
      const items = [...panel.current.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])')];
      if (!items.length) return;
      const first = items[0]!;
      const last = items[items.length - 1]!;
      const at = document.activeElement;
      if (e.shiftKey && (at === first || at === panel.current)) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && at === last) { e.preventDefault(); first.focus(); }
      else if (!panel.current.contains(at)) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", key);
    return () => document.removeEventListener("keydown", key);
  }, [requestClose]);

  return (
    <div className="fixed inset-0 z-50 flex justify-end" role="dialog" aria-modal="true" aria-label={d ? d.name : "Product"}>
      <div className="absolute inset-0 bg-ink/30" onClick={requestClose} aria-hidden />
      <div ref={panel} tabIndex={-1} className="relative flex h-full w-full max-w-[640px] flex-col overflow-y-auto bg-white shadow-md outline-none animate-in slide-in-from-right-8 duration-200">
        <header className="sticky top-0 z-10 flex items-start gap-3 border-b border-rule bg-white px-6 py-4">
          <div className="min-w-0 flex-1">
            <p className="text-meta text-ink-muted">{d ? [d.sku ?? "No SKU yet", d.supplier, d.supplierStyle && `style ${d.supplierStyle}`].filter(Boolean).join(" · ") : "Loading…"}</p>
            <h2 className="truncate text-[18px] font-semibold" title={d?.name}>{d?.name ?? ""}</h2>
          </div>
          <Link to={`/studio?tab=run&product=${id}`} className="mt-1 inline-flex h-8 shrink-0 items-center gap-1.5 rounded-lg border border-rule px-3 text-sm font-semibold hover:bg-paper focus-visible:outline focus-visible:outline-2 focus-visible:outline-focus">
            <Sparkles className="h-3.5 w-3.5" aria-hidden /> Open in Studio
          </Link>
          <button type="button" aria-label="Close" onClick={requestClose} className="rounded p-2 hover:bg-paper focus-visible:outline focus-visible:outline-2 focus-visible:outline-focus">
            <X className="h-5 w-5" />
          </button>
        </header>

        {error && <p className="p-6 text-label text-risk">{error}</p>}
        {!d && !error && <p className="flex items-center gap-2 p-6 text-label text-ink-muted"><Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Loading…</p>}

        {d && (
          <div className="flex flex-col gap-7 px-6 py-5">
            {/* Website status */}
            <section className={cn("flex flex-col gap-2 rounded-lg border p-4", onWebsite ? "border-ok/30 bg-ok-bg" : "border-warn/30 bg-warn-bg")}>
              <div className="flex flex-wrap items-center gap-3">
                <span className={cn("text-label font-semibold", onWebsite ? "text-ok" : "text-warn")}>
                  {onWebsite ? "On the website" : "Not on the website"}
                </span>
                <span className="ml-auto">
                  {d.hidden.byHand ? (
                    <Button size="sm" variant="outline" disabled={busy !== null} onClick={() => void setWebsite(false)}><Eye className="h-3.5 w-3.5" aria-hidden /> Show on website</Button>
                  ) : (
                    hiding === null && <Button size="sm" variant="outline" disabled={busy !== null} onClick={() => setHiding("")}><EyeOff className="h-3.5 w-3.5" aria-hidden /> Hide from website</Button>
                  )}
                </span>
              </div>
              {hiding !== null && !d.hidden.byHand && (
                <form className="flex flex-wrap items-center gap-2" onSubmit={(e) => { e.preventDefault(); void setWebsite(true); }}>
                  <input
                    autoFocus
                    value={hiding}
                    onChange={(e) => setHiding(e.target.value)}
                    maxLength={300}
                    placeholder="Why? (optional, helps the team later)"
                    aria-label="Why hide it from the website"
                    className="h-8 min-w-[220px] flex-1 rounded-md border border-rule bg-white px-2 text-meta focus-visible:outline focus-visible:outline-2 focus-visible:outline-focus"
                  />
                  <Button size="sm" type="submit" disabled={busy !== null}>Hide it</Button>
                  <Button size="sm" variant="ghost" type="button" onClick={() => setHiding(null)}>Cancel</Button>
                  <span className="w-full text-meta text-ink-muted">Clients stop seeing it; reps can still quote it. You can show it again any time.</span>
                </form>
              )}
              <ul className="flex list-disc flex-col gap-1 pl-5 text-meta text-ink">
                {d.hidden.byHand && d.handHide && (
                  <li>Hidden by {d.handHide.by ?? "someone"} on {when(d.handHide.at)}{d.handHide.reason ? `: “${d.handHide.reason}”` : "."}</li>
                )}
                {d.hidden.shipping && <li>Hidden because we can't quote shipping for it yet (no weight or warehouse from the supplier). It comes back by itself when that arrives.</li>}
                {GAP_ORDER.filter((g) => d.gaps.includes(g)).map((g) => <li key={g}>{GAPS[g]![1]}</li>)}
                {!d.ready && d.gaps.length === 0 && <li>Not checked since its last change; the nightly check will decide.</li>}
                {onWebsite && <li>Passes every website check.</li>}
              </ul>
              {!d.editingReady && <p className="text-meta text-ink-muted">Hiding from the website switches on shortly.</p>}
            </section>

            {/* Photos */}
            <section className="flex flex-col gap-2">
              <div className="flex items-baseline gap-3">
                <h3 className="text-label font-bold">Photos</h3>
                <span className="text-meta text-ink-muted">{d.images.length ? `${n(d.images.length)} from the supplier` : "None"}</span>
                {apparel && d.category && (
                  <Link to={`/studio?tab=photos&shelf=${d.category.slug}`} className="ml-auto inline-flex items-center gap-1 text-meta underline underline-offset-2">
                    Review photos by colour <ArrowRight className="h-3.5 w-3.5" aria-hidden />
                  </Link>
                )}
              </div>
              {d.images.length > 0 && (
                <div className="flex gap-2 overflow-x-auto pb-1">
                  {d.images.slice(0, 16).map((src) => (
                    <a key={src} href={src} target="_blank" rel="noreferrer" className="h-20 w-20 shrink-0 overflow-hidden rounded border border-rule bg-paper" title="Open full size">
                      <img src={src} alt="" loading="lazy" className="h-full w-full object-contain" />
                    </a>
                  ))}
                </div>
              )}
            </section>

            <div className="grid gap-6 md:grid-cols-2">
            {/* Name */}
            <section className="flex min-w-0 flex-col gap-2">
              <label htmlFor="pd-name" className="text-label font-bold">Name</label>
              <div className="flex gap-2">
                <input
                  id="pd-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={240}
                  className="h-10 min-w-0 flex-1 rounded-md border border-rule px-3 text-label focus-visible:outline focus-visible:outline-2 focus-visible:outline-focus"
                />
                <Button size="md" disabled={!nameDirty || !nameOk || busy !== null || !d.editingReady} onClick={() => void save("name", { name: tidyName }, "Name saved.")}>
                  {busy === "name" ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : "Save"}
                </Button>
              </div>
              {!d.editingReady && <span className="text-meta text-ink-muted">Editing names and descriptions switches on shortly. Moving shelves works now.</span>}
              {nameDirty && !nameOk && <span className="text-meta text-risk">A name needs 3 to 200 characters.</span>}
            </section>

            {/* Shelf */}
            <section className="flex min-w-0 flex-col gap-2">
              <h3 className="text-label font-bold">Shelf</h3>
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-label">{shelfLabel(d.category) ?? <span className="text-risk">Not on a shelf</span>}</span>
                {d.categorySetByHand && <span className="rounded bg-paper px-1.5 py-0.5 text-meta text-ink-muted">set by hand</span>}
                <span className="ml-auto">
                  <ShelfPicker
                    shelves={shelves}
                    label="Move to…"
                    current={d.category?.slug}
                    disabled={busy !== null}
                    onOpenChange={onPickerOpenChange}
                    onPick={(slug) => {
                      const to = shelves.find((s) => s.slug === slug);
                      void save("shelf", { categorySlug: slug }, `Moved to ${to ? shelfLabel({ slug, name: to.name, department: to.department }) : "the new shelf"}.`);
                    }}
                  />
                </span>
              </div>
              <p className="text-meta text-ink-muted">A move is kept when the catalogue is re-filed each night.</p>
            </section>
            </div>

            {/* Description */}
            <section className="flex flex-col gap-3">
              <div className="flex items-baseline gap-3">
                <h3 className="text-label font-bold">Description</h3>
                <span className="text-meta text-ink-muted">
                  {d.descriptionSource === "supplier" ? "The website shows the supplier's words" : d.descriptionSource === "edited" ? "The website shows words a person wrote" : "The website shows our written draft"}
                </span>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="flex flex-col gap-1.5">
                  <h4 className="text-meta font-semibold uppercase tracking-wide text-ink-muted">Supplier's words</h4>
                  <p className="max-h-[220px] overflow-y-auto whitespace-pre-line rounded-md bg-paper p-3 text-meta text-ink-muted">{d.supplierDescription?.trim() || "The supplier gave no description."}</p>
                  {d.supplierDescription && d.supplierDescription.trim() !== desc.trim() && (
                    <button type="button" className="self-start text-meta underline underline-offset-2" onClick={() => setDesc(d.supplierDescription ?? "")}>Start from these words</button>
                  )}
                </div>
                <div className="flex flex-col gap-1.5">
                  <h4 className="flex items-center gap-2 text-meta font-semibold uppercase tracking-wide text-ink-muted">
                    Ours {descDirty && <span className="rounded bg-warn-bg px-1.5 normal-case tracking-normal text-warn">not saved</span>}
                  </h4>
                  <textarea
                    value={desc}
                    onChange={(e) => setDesc(e.target.value)}
                    rows={9}
                    aria-label="Our description"
                    className="w-full resize-y rounded-md border border-rule p-3 text-meta leading-relaxed focus-visible:outline focus-visible:outline-2 focus-visible:outline-focus"
                  />
                  <span className={cn("text-meta", descDirty && !descOk ? "text-risk" : "text-ink-muted")}>
                    {n(descLen)} characters{descLen < 40 ? " · needs at least 40" : descLen > 2000 ? " · keep it under 2,000" : ""}
                  </span>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <Button size="sm" disabled={!descDirty || !descOk || busy !== null || !d.editingReady} onClick={() => void save("desc", { description: desc }, onWebsite ? "Description saved. The website shows it now." : "Description saved.")}>
                  {busy === "desc" ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : <Check className="h-3.5 w-3.5" aria-hidden />} Save description
                </Button>
                {descDirty && <button type="button" className="text-meta underline underline-offset-2" onClick={() => setDesc(d.description ?? "")}>Undo my changes</button>}
                <span className="text-meta text-ink-muted">{d.editingReady ? "Saved words are never replaced by the description writer." : "Editing names and descriptions switches on shortly. Moving shelves works now."}</span>
              </div>
            </section>

            {/* Suppliers */}
            <section className="flex flex-col gap-2">
              <h3 className="text-label font-bold">Who supplies it</h3>
              <div className="overflow-hidden rounded-lg border border-rule">
                <table className="w-full text-left text-meta">
                  <thead className="bg-paper text-ink-muted">
                    <tr>
                      <th scope="col" className="px-3 py-2 font-semibold">Supplier</th>
                      <th scope="col" className="px-3 py-2 font-semibold">Style</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.offers.map((o, i) => (
                      <tr key={`${o.supplier}-${o.style}-${i}`} className="border-t border-rule">
                        <td className="px-3 py-2">
                          {o.supplier}
                          {o.preferred && <span className="ml-2 rounded bg-ok-bg px-1.5 py-0.5 text-meta font-semibold text-ok">used</span>}
                          {!o.active && <span className="ml-2 rounded bg-paper px-1.5 py-0.5 text-meta text-ink-muted">not offered now</span>}
                        </td>
                        <td className="px-3 py-2">{o.style ?? "—"}</td>
                      </tr>
                    ))}
                    {d.offers.length === 0 && <tr><td colSpan={2} className="px-3 py-2 text-ink-muted">No supplier linked.</td></tr>}
                  </tbody>
                </table>
              </div>
              {d.colours.length > 0 && (
                <p className="text-meta text-ink-muted"><b className="font-semibold text-ink">Colours:</b> {d.colours.slice(0, 40).join(", ")}{d.colours.length > 40 ? ` and ${n(d.colours.length - 40)} more` : ""}</p>
              )}
            </section>

            {/* History */}
            {(d.moves.length > 0 || d.edits.length > 0) && (
              <section className="flex flex-col gap-2">
                <h3 className="text-label font-bold">History</h3>
                <ul className="flex flex-col gap-1 text-meta text-ink-muted">
                  {[
                    ...d.moves.map((m) => ({ at: m.at, text: `${m.by ?? "Someone"} moved it ${m.from ? `from ${m.from} ` : ""}to ${m.to}` })),
                    ...d.edits.map((e) => ({ at: e.at, text: `${e.by ?? "Someone"} ${e.field === "website" ? "changed whether it shows on the website" : `edited the ${e.field}`}` })),
                  ]
                    .sort((a, b) => b.at.localeCompare(a.at))
                    .slice(0, 5)
                    .map((h, i) => <li key={i}>{when(h.at)} · {h.text}</li>)}
                </ul>
              </section>
            )}

            <p className="flex items-center gap-1 border-t border-rule pt-4 text-meta text-ink-muted">
              <ExternalLink className="h-3 w-3" aria-hidden /> Photos open the supplier's original in a new tab.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
